import {
  CSSProperties,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { AccordianItem } from 'components/Accordian';
import { useAccordianContext } from 'components/Accordian/context';

export interface AccordianItemRenderHook {
  collapseRef: React.MutableRefObject<HTMLDivElement | null>;
  handleClick: () => void;
  className: string;
  contentStyle: CSSProperties;
}

export default function useAccordianItemRender(
  item: AccordianItem,
): AccordianItemRenderHook {
  const { openId, onClick, milliseconds, disabled } = useAccordianContext();

  const [maxHeight, setMaxHeight] = useState<string>('0px');

  const collapseRef = useRef<HTMLDivElement | null>(null);

  const isActive = useMemo(() => openId === item.id, [openId, item.id]);

  const className = useMemo(
    () =>
      ['flex flex-col w-full overflow-hidden', item.wrapperClassName]
        .filter(Boolean)
        .join(' '),
    [item.wrapperClassName],
  );

  const handleClick = useCallback(() => {
    if (disabled) return;
    onClick(item.id);
    if (isActive) return;
    setTimeout(() => setMaxHeight('0px'), 10);
  }, [disabled, item.id, onClick, isActive]);

  const style = useMemo(
    () => ({
      maxHeight,
      transitionDuration: `${milliseconds}ms`,
    }),
    [maxHeight, milliseconds],
  );

  useEffect(() => {
    const clientHeight = `${collapseRef.current?.clientHeight || 100}px`;
    if (!isActive) {
      setMaxHeight(clientHeight);
      setTimeout(() => setMaxHeight('0px'), 10);
      return;
    }
    setTimeout(() => {
      setMaxHeight(clientHeight);
    }, 10);
    setTimeout(() => {
      setMaxHeight('none');
    }, milliseconds + 100);
  }, [isActive, milliseconds]);

  return {
    collapseRef,
    handleClick,
    className,
    contentStyle: style,
  };
}
